import React, { useEffect, useState } from "react"
import { Formik, Form, Field, ErrorMessage, FieldArray, getIn } from "lib/formik"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Row,
  Spinner,
  Table,
} from "reactstrap"
import Switch from "components/Shared/Switch"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { showAlert } from "store/reducers/alertReducer"
import BackButton from "components/Shared/BackButton"
import { getCategories } from "store/actions/categoriesAction"
import Select from "react-select"
import { allOption, disablePastDates } from "components/Common/constants"
import { getAllBranchs } from "store/actions/usersAction"
import { DatePicker } from "rsuite"
import {
  addItem,
  calculateComboPrice,
  getItemsDetails,
  getItemsListDropdown,
  updateItem,
} from "store/actions/itemsAction"
import { addComboSchemas } from "components/Common/validation"
import Spinners from "components/Shared/Spinners"
import { GET_ITEM_DETAILS } from "helpers/url_helper"
import { post } from "helpers/api_helper"
import {
  getImagePreviewSrc,
  handleFileChange,
} from "components/Shared/fileHandlers"
import {
  getItemGroupDetails,
  getItemGroupsList,
} from "store/actions/itemGroupsAction"
import { ShimmerThumbnail } from "react-shimmer-effects"
import { getCountryList } from "store/actions/countriesAction"
import { useTranslation } from "react-i18next"
import { itemHeight, itemMaxSize, itemWidth } from "components/Common/ImageSize"
import TranslatedFileInput from "components/Shared/TranslatedFileInput"
import TextError from "components/Shared/TextError"
import { resetItems } from "store/reducers/itemsReducer"

const buildItemSelectOptions = (itemsListDropdown, language) => {
  return (itemsListDropdown || []).map(item => ({
    label: language === "sa" ? item.nameAr : item.name,
    value: item._id,
    price: item.price,
  }))
}

const getSelectedModifierGroupIds = (modifiers, excludeModifierIndex) => {
  const ids = new Set()
  modifiers?.forEach((row, idx) => {
    if (idx === excludeModifierIndex) return
    const selected = row?.modifier
    if (Array.isArray(selected)) {
      selected.forEach(entry => entry?.value && ids.add(entry.value))
    }
  })
  return ids
}

const buildModifierGroupOptions = (
  modifierGroupsList,
  modifiers,
  modifierIndex,
  language
) => {
  const takenIds = getSelectedModifierGroupIds(modifiers, modifierIndex)
  return (modifierGroupsList || [])
    .filter(mod => !takenIds.has(mod._id))
    .map(mod => ({
      label:
        language === "sa"
          ? mod.modifierGroupId?.nameAr
          : mod.modifierGroupId?.name,
      value: mod._id,
    }))
}

const initialValues = {
  name: "",
  nameAr: "",
  description: "",
  descriptionAr: "",
  displayOrder: null,
  imageType: "UPLOAD",
  imageUrl: "",
  imageFile: "",
  personCountPerServing: "",
  disableForPickup: false,
  disableForDelivery: false,
  price: "",
  originalPrice: "",
  discount: "",
  calories: "",
  calorieCount: "",
  categoryId: "",
  isCombo: true,
  isDiscountAllowed: false,
  disabledBranches: [],
  hasSpecificDateAndTime: false,
  startDateAndTime: "",
  endDateAndTime: "",
  items: [{ item: null, quantity: 1, modifiers: [{ modifier: null }] }],
  itemGroups: [],
  isPriceCustomize: false,
  isItemGroup: false,
  selectedGroupItems: [],
  countryCode: null,
}

const AddCombo = ({ mode }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = ` ${mode === "add" ? t("Add") : t("Edit")} ${t(
    "Combo "
  )}`

  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")
  const [formValues, setFormValues] = useState(initialValues)

  const { loading, isLoading, itemDetails } = useSelector(state => state.Items)

  const { allCategories } = useSelector(state => state.Categories)
  const { branchsList } = useSelector(state => state.Users)
  const { itemsListDropdown } = useSelector(state => state.Items)
  const { countryList } = useSelector(state => state.Countries)
  const { isImageCompressing } = useSelector(state => state.Common)

  const [isAllSelected, setIsAllSelected] = useState(false)
  const [modifierGroupsList, setModifierGroupsList] = useState([])
  const {
    loading: itemGroupLoading,
    itemGroupsList,
    itemGroupDetails,
  } = useSelector(state => state.ItemGroups)

  useEffect(() => {
    getCategoriesList()
    fetchAllBranches()
    getItemComboList()
    fetchCountryList()
    if (mode === "edit") {
      fetchComboDetails(id)
    }
  }, [])

  useEffect(() => {
    if (itemDetails && !loading && mode === "edit") {
      const {
        name,
        nameAr,
        conditions,
        description,
        descriptionAr,
        imageUrlType,
        imageUrl,
        displayOrder,
        originalPrice,
        price,
        calories,
        calorieCount,
        personCountPerServing,
        disableForPickup,
        disableForDelivery,
        isDiscountAllowed,
        discount,
        isCombo,
        disabledBranches,
        outOfStockForBranches,
        categoryId,
        modifierGroups,
        items,
        itemGroups,
        image,
        countryCode,
      } = itemDetails

      const updatedCategory = {
        label: language === "sa" ? categoryId?.nameAr : categoryId?.name,
        value: categoryId?._id,
      }
      const updatedDisabledBranches = disabledBranches?.map(item => ({
        label: language === "sa" ? item.nameAr : item.name,
        value: item._id,
      }))

      const transformedItems = items?.map(item => {
        const transformedItem = {
          item: {
            label:
              language === "sa" ? item?.itemId?.nameAr : item?.itemId?.name,
            value: item?.itemId?._id,
            price: item?.itemId?.price,
          },
          quantity: item.quantity,
        }

        if (item?.modifierGroups && item?.modifierGroups?.length > 0) {
          transformedItem.modifiers = [
            {
              modifier: item?.modifierGroups?.map(modifier => ({
                label: language === "sa" ? modifier.nameAr : modifier.name,
                value: modifier._id,
              })),
            },
          ]
        } else {
          transformedItem.modifiers = [
            {
              modifier: [],
            },
          ]
        }

        return transformedItem
      })

      // ITEMS GROUPS
      const updatedItemGroups = itemGroups?.[0]?.itemGroup
        ? {
            label:
              language === "sa"
                ? itemGroups[0]?.itemGroup?.nameAr
                : itemGroups[0]?.itemGroup?.name || "",
            value: itemGroups[0]?.itemGroup?._id || "",
          }
        : {}

      const updatedIsItemGroup = itemGroups?.length > 0 ? true : false
      let updatedSelectedGroupItems = []
      let updatedPriceCustomize = false
      if (itemGroups?.length > 0) {
        const updatedItems = itemGroups[0]?.items?.map(item => ({
          _id: item?.itemId?._id,
          name: language === "sa" ? item?.itemId?.nameAr : item?.itemId?.name,
          originalPrice: item?.originalPrice,
          price: item?.price,
        }))
        updatedSelectedGroupItems = updatedItems
        updatedPriceCustomize = itemGroups[0]?.isPriceCustomize
      }

      const matchedCountry = countryList.find(
        item => item.countryCode === countryCode
      )
      var updatedCountryCode = null
      if (matchedCountry) {
        const { name, nameAr, countryCode } = matchedCountry
        updatedCountryCode = {
          label: language === "sa" ? nameAr : name,
          value: countryCode,
        }
      }

      getCategoriesList(countryCode)

      if (countryCode) {
        fetchComboItems(countryCode)
      }

      setFormValues({
        ...formValues,
        name,
        nameAr,
        description,
        descriptionAr,
        displayOrder,
        imageType: imageUrlType,
        imageUrl,
        personCountPerServing,
        disableForPickup,
        disableForDelivery,
        price,
        originalPrice,
        calories,
        calorieCount,
        categoryId: updatedCategory,
        isCombo: true,
        isDiscountAllowed,
        discount,
        disabledBranches: updatedDisabledBranches,
        hasSpecificDateAndTime: conditions?.hasSpecificDateAndTime,
        startDateAndTime: new Date(conditions?.startDateAndTime) || "",
        endDateAndTime: new Date(conditions?.endDateAndTime) || "",
        items: transformedItems,
        itemGroups: updatedItemGroups,
        isItemGroup: updatedIsItemGroup,
        selectedGroupItems: updatedSelectedGroupItems,
        isPriceCustomize: updatedPriceCustomize,
        countryCode: updatedCountryCode,
      })
    }
  }, [itemDetails, countryList, language])

  const getCategoriesList = countryCode => {
    const isCombo = true
    dispatch(getCategories({ isCombo, countryCode }))
  }
  const fetchAllBranches = () => {
    dispatch(getAllBranchs())
  }

  const fetchCountryList = () => {
    dispatch(getCountryList())
  }

  const fetchComboItems = countryCode => {
    if (!countryCode) return

    const payload = {
      countryCode:
        typeof countryCode === "string"
          ? countryCode.toUpperCase()
          : countryCode,
    }
    dispatch(getItemsListDropdown(payload))
  }
  useEffect(() => {
    dispatch(resetItems())
  }, [])

  const fetchComboDetails = itemId => {
    const payload = {
      id: itemId,
    }
    dispatch(getItemsDetails(payload))
  }

  const getItemComboList = () => {
    dispatch(getItemGroupsList())
  }
  const getItemsFromItemGroup = (itemGroupId, setFieldValue) => {
    const payload = {
      id: itemGroupId,
    }
    dispatch(getItemGroupDetails(payload))
      .then(action => {
        const { statusCode, status, msg, data } = action?.payload
        if (statusCode === 200 && status) {
          const { items } = data
          setFieldValue("selectedGroupItems", items)
        }
      })
      .catch(error => {})
  }

  // GET MODIFIER GROUP LIST W.R.T. ITEM DETAILS
  const getModifierGroupsList = async itemId => {
    const payload = {
      id: itemId,
    }
    const response = await post(GET_ITEM_DETAILS, payload)
    if (response?.status && response?.statusCode === 200) {
      const { modifierGroups } = response?.data
      setModifierGroupsList(modifierGroups)
    }
  }

  const branchListOptions = branchsList?.map(item => ({
    label: language === "sa" ? item.nameAr : item.name,
    value: item._id,
  }))

  const options = isAllSelected
    ? branchListOptions
    : [allOption, ...branchListOptions]

  const handleSubmit = values => {
    const {
      name,
      nameAr,
      description,
      descriptionAr,
      displayOrder,
      imageType,
      imageUrl,
      imageFile,
      personCountPerServing,
      disableForPickup,
      disableForDelivery,
      price,
      originalPrice,
      calories,
      calorieCount,
      categoryId,
      isCombo,
      isDiscountAllowed,
      discount,
      disabledBranches,
      hasSpecificDateAndTime,
      startDateAndTime,
      endDateAndTime,
      items,
      itemGroups,
      isPriceCustomize,
      isItemGroup,
      selectedGroupItems,
      countryCode,
    } = values

    const category = categoryId.value

    let data = new FormData()
    data.append("name", name)
    data.append("nameAr", nameAr)
    data.append("description", description)
    data.append("descriptionAr", descriptionAr)
    data.append("displayOrder", displayOrder)
    data.append("imageUrl", imageUrl)
    data.append("imageUrlType", imageType)
    // data.append("status", "1")
    data.append("personCountPerServing", personCountPerServing)
    data.append("disableForPickup", disableForPickup)
    // data.append("disableForDelivery", "false")
    data.append("price", price)
    data.append("isDiscountAllowed", isDiscountAllowed)
    isDiscountAllowed && data.append("discount", discount)
    data.append("originalPrice", originalPrice)
    data.append("calories", calories)
    data.append("calorieCount", calories) // we have to calories for both calories & calories count
    data.append("categoryId", category)
    data.append("isCombo", "true")
    data.append("countryCode", countryCode?.value?.toUpperCase())

    if (disabledBranches.length === 0) {
      data.append(`disabledBranches[]`, [])
    } else {
      disabledBranches?.forEach((element, index) => {
        data.append(`disabledBranches[${index}]`, element.value)
      })
    }
    // Append items
    items?.forEach((item, index) => {
      data.append(`items[${index}][itemId]`, item.item.value) // Assuming item.item is the Select value object
      data.append(`items[${index}][quantity]`, item.quantity)

      // Append modifiers if any
      if (item?.modifiers && item?.modifiers?.length > 0) {
        item.modifiers.forEach((modifierGroup, modifierIndex) => {
          if (modifierGroup.modifier && Array.isArray(modifierGroup.modifier)) {
            modifierGroup.modifier.forEach((mod, subIndex) => {
              data.append(
                `items[${index}][modifierGroups][${subIndex}]`,
                mod.value
              )
            })
          }
        })
      }
    })

    data.append("conditions[hasSpecificDateAndTime]", hasSpecificDateAndTime)

    if (hasSpecificDateAndTime) {
      data.append("conditions[startDateAndTime]", startDateAndTime)
      data.append("conditions[endDateAndTime]", endDateAndTime)
    }

    // ITEM GROUP

    if (Object.keys(itemGroups).length > 0 && isItemGroup) {
      data.append("itemGroups[0][itemGroup]", itemGroups.value)
      data.append("itemGroups[0][isPriceCustomize]", isPriceCustomize)

      if (selectedGroupItems.length > 0) {
        selectedGroupItems?.forEach((element, index) => {
          // data.append(`disabledBranches[${index}]`, element.value)
          data.append(`itemGroups[0][items][${index}][itemId]`, element._id)
          data.append(
            `itemGroups[0][items][${index}][originalPrice]`,
            element.originalPrice
          )
          data.append(`itemGroups[0][items][${index}][price]`, element.price)
        })
      }
    }

    data.append("image", imageFile)
    if (mode === "add") {
      dispatch(addItem(data))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            navigate(`/menus/combos`)
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    } else if (mode === "edit") {
      data.append("id", id)
      dispatch(updateItem(data))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            navigate(-1)
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    }
  }

  const applyDiscountedPrice = (
    setFieldValue,
    { originalPrice, discount, isDiscountAllowed }
  ) => {
    if (!isDiscountAllowed) return
    const base = parseFloat(originalPrice)
    if (Number.isNaN(base)) return
    const discountPct = parseFloat(discount)
    const pct = Number.isNaN(discountPct) ? 0 : discountPct
    const discountedPrice = base * (1 - pct / 100)
    setFieldValue("price", Math.round(Math.max(discountedPrice, 0)))
  }

  const calculateTotalPrice = (
    items,
    currentIndex,
    newQuantity,
    setFieldValue,
    values
  ) => {
    const updatedItems = items.map((item, index) => ({
      itemId: item?.item?.value,
      quantity: index === currentIndex ? newQuantity : item.quantity || 1,
      modifierGroups:
        Array.isArray(item.modifiers) && item.modifiers.length > 0
          ? Array.isArray(item.modifiers[0]?.modifier)
            ? item.modifiers[0].modifier.map(mod => mod.value)
            : []
          : [],
    }))

    const payload = {
      items: updatedItems,
      isDiscountAllowed: values.isDiscountAllowed,
      discount: Number(values.discount) || 0,
    }
    dispatch(calculateComboPrice(payload))
      .then(action => {
        const { statusCode, status, data } = action?.payload || {}
        if (statusCode === 200 && status && data) {
          const { originalPrice, calories, price } = data
          setFieldValue("originalPrice", originalPrice)
          setFieldValue("calories", calories)
          if (values.isDiscountAllowed) {
            applyDiscountedPrice(setFieldValue, {
              originalPrice,
              discount: values.discount,
              isDiscountAllowed: true,
            })
          } else {
            setFieldValue("price", price)
          }
        }
      })
      .catch(() => {})
  }

  if (loading && mode === "edit") return <Spinners />

  return (
    <div className="page-content add_item_page">
      <div className="container-fluid">
        <Row>
          <Col xl={12}>
            <Card>
              <CardBody>
                <CardTitle className="d-flex align-items-center">
                  <BackButton />
                  {mode === "add" ? t("Add") : t("Edit")} {t("Combo")}
                </CardTitle>
                <Formik
                  enableReinitialize={true}
                  initialValues={formValues}
                  onSubmit={handleSubmit}
                  validationSchema={addComboSchemas(mode)}
                >
                  {({ errors, touched, values, setFieldValue }) => (
                    <Form>
                      <div className="row">
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="name">
                              <span className="text-danger">*</span>{" "}
                              {t("Name(English)")}
                            </label>
                            <Field
                              type="text"
                              id="name"
                              name="name"
                              placeholder={t("Name(English)")}
                              className={`form-control ${
                                touched.name && errors.name ? "is-invalid" : ""
                              }`}
                            />
                            <TextError name="name" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="nameAr">
                              <span className="text-danger">*</span>{" "}
                              {t("Name(عربي)")}
                            </label>
                            <Field
                              type="text"
                              id="nameAr"
                              style={{ direction: "rtl" }}
                              name="nameAr"
                              placeholder={t("Name(عربي)")}
                              className={`form-control ${
                                touched.nameAr && errors.nameAr
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="nameAr" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="description">
                              {t("Description(English)")}
                            </label>
                            <Field
                              // type="text"
                              as="textarea"
                              rows={1}
                              id="description"
                              name="description"
                              placeholder={t("Description(English)")}
                              className={`form-control ${
                                touched.description && errors.description
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="description" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="descriptionAr">
                              {t("Description(عربي)")}
                            </label>
                            <Field
                              as="textarea"
                              rows={1}
                              id="descriptionAr"
                              style={{ direction: "rtl" }}
                              name="descriptionAr"
                              placeholder={t("Description(عربي)")}
                              className={`form-control ${
                                touched.descriptionAr && errors.descriptionAr
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="descriptionAr" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="mt-2">
                            <label htmlFor="countryCode">
                              <span className="text-danger">*</span>{" "}
                              {t("Select Country")}
                            </label>
                            <Select
                              value={values.countryCode}
                              onChange={selectedValue => {
                                setFieldValue("countryCode", selectedValue)
                                getCategoriesList(selectedValue.value)
                                dispatch(resetItems())
                                setFieldValue("categoryId", "")
                                setFieldValue("items", [
                                  {
                                    item: null,
                                    quantity: 1,
                                    modifiers: [{ modifier: null }],
                                  },
                                ])
                                fetchComboItems(selectedValue.value)
                              }}
                              options={countryList?.map(item => ({
                                label:
                                  language === "sa" ? item.nameAr : item.name,
                                value: item.countryCode,
                              }))}
                              placeholder={t("Select Country")}
                              className={`select2-selection ${
                                touched.countryCode && errors.countryCode
                                  ? "danger_color_border"
                                  : ""
                              } `}
                            />
                            <TextError name="countryCode" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="mt-2">
                            <label htmlFor="categoryId">
                              <span className="text-danger">*</span>{" "}
                              {t("Select Category")}
                            </label>
                            <Select
                              value={values.categoryId}
                              onChange={item => {
                                setFieldValue("categoryId", item)
                              }}
                              options={
                                values.countryCode
                                  ? allCategories?.map(item => ({
                                      label:
                                        language === "sa"
                                          ? item.nameAr
                                          : item.name,
                                      value: item._id,
                                    }))
                                  : []
                              }
                              className={`select2-selection ${
                                touched.categoryId && errors.categoryId
                                  ? "danger_color_border"
                                  : ""
                              } `}
                              placeholder={t("Select Category")}
                              noOptionsMessage={() =>
                                !values.countryCode
                                  ? t("Please select country first")
                                  : t("No options")
                              }
                            />
                            <TextError name="categoryId" />
                          </div>
                        </div>

                        <div className="col-lg-12">
                          <div className="mt-2">
                            <label htmlFor="items">
                              <span className="text-danger">*</span>{" "}
                              {t("Select Item And Quantity")}
                            </label>

                            <FieldArray name="items">
                              {({ push, remove }) => (
                                <>
                                  {values.items &&
                                    values.items.length > 0 &&
                                    values.items.map((rule, index) => (
                                      <div className="row mt-2" key={index}>
                                        <div className="col">
                                          <Select
                                            value={values.items[index].item}
                                            onChange={selectedValue => {
                                              // FETCH MODIFIER GROUPS
                                              getModifierGroupsList(
                                                selectedValue.value
                                              )
                                              setFieldValue(
                                                `items[${index}].item`,
                                                selectedValue
                                              )

                                              // Create a new items array with the updated item
                                              const updatedItems =
                                                values.items.map((item, idx) =>
                                                  idx === index
                                                    ? {
                                                        ...item,
                                                        item: selectedValue,
                                                      }
                                                    : item
                                                )

                                              calculateTotalPrice(
                                                updatedItems,
                                                index,
                                                values.items[index].quantity,
                                                setFieldValue,
                                                values
                                              )
                                            }}
                                            options={buildItemSelectOptions(
                                              itemsListDropdown,
                                              language
                                            )}
                                            placeholder={t("Select Item")}
                                            className={`select2-selection ${
                                              getIn(
                                                touched,
                                                `items[${index}].item`
                                              ) &&
                                              getIn(
                                                errors,
                                                `items[${index}].item`
                                              )
                                                ? "danger_color_border"
                                                : ""
                                            }`}
                                          />
                                          <TextError
                                            name={`items[${index}].item`}
                                            component="div"
                                            className="text-danger"
                                          />
                                        </div>
                                        <div className="col">
                                          <Field
                                            name={`items[${index}].quantity`}
                                            placeholder={t("Quantity")}
                                            onChange={e => {
                                              const rawValue = e.target.value
                                              if (
                                                rawValue.includes(".") ||
                                                rawValue.includes(",")
                                              ) {
                                                return
                                              }
                                              const qty = Number(rawValue)
                                              if (
                                                rawValue !== "" &&
                                                (!Number.isInteger(qty) ||
                                                  qty < 1)
                                              ) {
                                                return
                                              }

                                              // Create a new items array with the updated quantity
                                              const updatedItems =
                                                values.items.map((item, idx) =>
                                                  idx === index
                                                    ? { ...item, quantity: qty }
                                                    : item
                                                )

                                              setFieldValue(
                                                `items[${index}].quantity`,
                                                qty
                                              )
                                              calculateTotalPrice(
                                                updatedItems,
                                                index,
                                                qty,
                                                setFieldValue,
                                                {
                                                  ...values,
                                                  items: updatedItems,
                                                }
                                              )
                                            }}
                                            type="number"
                                            min="1"
                                            step="1"
                                            className={`form-control ${
                                              getIn(
                                                touched,
                                                `items[${index}].quantity`
                                              ) &&
                                              getIn(
                                                errors,
                                                `items[${index}].quantity`
                                              )
                                                ? "danger_color_border"
                                                : ""
                                            }`}
                                          />
                                          <TextError
                                            name={`items[${index}].quantity`}
                                            component="div"
                                            className="text-danger"
                                          />
                                        </div>
                                        {/* SHOW ALL MODIFIERS GROUPS */}
                                        <FieldArray
                                          name={`items[${index}].modifiers`}
                                        >
                                          {({ remove: removeModifier }) => (
                                            <>
                                              {values.items[index].modifiers &&
                                                values.items[
                                                  index
                                                ].modifiers.map(
                                                  (modifier, modifierIndex) => (
                                                    <div
                                                      className="col"
                                                      key={modifierIndex}
                                                    >
                                                      <Select
                                                        isMulti={true}
                                                        value={
                                                          modifier.modifier
                                                        }
                                                        onChange={selectedValue => {
                                                          setFieldValue(
                                                            `items[${index}].modifiers[${modifierIndex}].modifier`,
                                                            selectedValue
                                                          )
                                                          // Create a new items array with the updated item
                                                          const updatedItems =
                                                            values.items.map(
                                                              (item, idx) =>
                                                                idx === index
                                                                  ? {
                                                                      ...item,
                                                                      modifiers:
                                                                        item.modifiers.map(
                                                                          (
                                                                            mod,
                                                                            modIdx
                                                                          ) =>
                                                                            modIdx ===
                                                                            modifierIndex
                                                                              ? {
                                                                                  ...mod,
                                                                                  modifier:
                                                                                    selectedValue,
                                                                                }
                                                                              : mod
                                                                        ),
                                                                    }
                                                                  : item
                                                            )

                                                          calculateTotalPrice(
                                                            updatedItems,
                                                            index,
                                                            values.items[index]
                                                              .quantity,
                                                            setFieldValue,
                                                            values
                                                          )
                                                        }}
                                                        options={buildModifierGroupOptions(
                                                          modifierGroupsList,
                                                          values.items[index]
                                                            .modifiers,
                                                          modifierIndex,
                                                          language
                                                        )}
                                                        placeholder={t(
                                                          "Select Modifiers Groups"
                                                        )}
                                                        hideSelectedOptions={
                                                          true
                                                        }
                                                        closeMenuOnSelect={
                                                          false
                                                        }
                                                        className={`select2-selection ${
                                                          getIn(
                                                            touched,
                                                            `items[${index}].modifiers[${modifierIndex}].modifier`
                                                          ) &&
                                                          getIn(
                                                            errors,
                                                            `items[${index}].modifiers[${modifierIndex}].modifier`
                                                          )
                                                            ? "danger_color_border"
                                                            : ""
                                                        }`}
                                                      />
                                                    </div>
                                                  )
                                                )}
                                            </>
                                          )}
                                        </FieldArray>
                                        {values.items.length > 1 && (
                                          <div
                                            className="col"
                                            style={{ maxWidth: 60 }}
                                          >
                                            <Button
                                              color="danger"
                                              type="button"
                                              onClick={() => {
                                                remove(index)
                                                // Recalculate total price after removing the item
                                                const updatedItems =
                                                  values.items.filter(
                                                    (_, idx) => idx !== index
                                                  )
                                                calculateTotalPrice(
                                                  updatedItems,
                                                  -1,
                                                  0,
                                                  setFieldValue,
                                                  values
                                                )
                                              }}
                                            >
                                              <i className="mdi mdi-delete "></i>
                                            </Button>
                                          </div>
                                        )}
                                        {/* <div
                                          className="col"
                                          style={{ maxWidth: 60 }}
                                        >
                                          <Button
                                            color="primary"
                                            type="button"
                                            id={`addCombo${index}`}
                                            disabled={
                                              values?.items[index]?.modifiers
                                                ?.length > 0
                                            }
                                            onClick={() => {
                                              // Push a new modifier to the modifiers array
                                              setFieldValue(
                                                `items[${index}].modifiers`,
                                                [
                                                  ...values.items[index]
                                                    .modifiers,
                                                  { modifier: null },
                                                ]
                                              )
                                            }}
                                          >
                                            <i className="bx bx-plus-medical"></i>
                                          </Button>
                                        </div> */}
                                      </div>
                                    ))}
                                  <div className="mt-3">
                                    <Button
                                      type="button"
                                      onClick={() =>
                                        push({
                                          item: null,
                                          quantity: 1,
                                          modifiers: [{ modifier: null }],
                                        })
                                      }
                                    >
                                      {t("Add More Item")}
                                    </Button>
                                  </div>
                                </>
                              )}
                            </FieldArray>
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="displayOrder">
                              {" "}
                              <span className="text-danger">*</span>{" "}
                              {t("Display Order")}
                            </label>
                            <Field
                              type="text"
                              id="displayOrder"
                              name="displayOrder"
                              placeholder={t("Display Order")}
                              className={`form-control ${
                                touched.displayOrder && errors.displayOrder
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="displayOrder" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="isDiscountAllowed">
                              {t("Is Discount Allowed")}
                            </label>
                            <Switch
                              isChecked={values.isDiscountAllowed}
                              onClickHandler={() => {
                                const nextAllowed = !values.isDiscountAllowed
                                setFieldValue("isDiscountAllowed", nextAllowed)
                                if (nextAllowed) {
                                  applyDiscountedPrice(setFieldValue, {
                                    originalPrice: values.originalPrice,
                                    discount: values.discount,
                                    isDiscountAllowed: true,
                                  })
                                } else {
                                  setFieldValue(
                                    "price",
                                    values.originalPrice ?? values.price
                                  )
                                }
                              }}
                            />
                          </div>
                        </div>

                        {values.isDiscountAllowed && (
                          <div className="col-lg-6">
                            <div className="form_field">
                              <label htmlFor="discount">
                                <span className="text-danger">* </span>
                                {t("Discount (In %)")}
                              </label>
                              <Field
                                type="number"
                                id="discount"
                                name="discount"
                                min="0"
                                max="100"
                                onChange={e => {
                                  const discountValue = e.target.value
                                  setFieldValue("discount", discountValue)
                                  applyDiscountedPrice(setFieldValue, {
                                    originalPrice: values.originalPrice,
                                    discount: discountValue,
                                    isDiscountAllowed: true,
                                  })
                                }}
                                placeholder={t("Discount (In %)")}
                                className={`form-control ${
                                  touched.discount && errors.discount
                                    ? "is-invalid"
                                    : ""
                                }`}
                              />
                              <TextError
                                name="discount"
                                component="div"
                                className="text-danger"
                              />
                            </div>
                          </div>
                        )}

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="price">
                              <span className="text-danger">* </span>
                              {t("Price")}
                            </label>
                            <Field
                              type="text"
                              id="price"
                              name="price"
                              placeholder={t("e.g. 10")}
                              readOnly={values.isDiscountAllowed}
                              className={`no_cursor form-control ${
                                touched.price && errors.price
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="price" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="originalPrice">
                              {" "}
                              <span className="text-danger">*</span>{" "}
                              {t("Original Price")}
                            </label>
                            <Field
                              type="text"
                              id="originalPrice"
                              name="originalPrice"
                              placeholder={t("e.g. 10")}
                              onChange={e => {
                                const originalPriceValue = e.target.value
                                setFieldValue(
                                  "originalPrice",
                                  originalPriceValue
                                )
                                applyDiscountedPrice(setFieldValue, {
                                  originalPrice: originalPriceValue,
                                  discount: values.discount,
                                  isDiscountAllowed: values.isDiscountAllowed,
                                })
                              }}
                              className={`no_cursor form-control ${
                                touched.originalPrice && errors.originalPrice
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="originalPrice" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="calories">
                              {t("Calories For Display")}
                            </label>
                            <Field
                              type="text"
                              id="calories"
                              name="calories"
                              // readOnly
                              placeholder={t("E.g. 150- 200")}
                              className="form-control"
                            />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="personCountPerServing">
                              <span className="text-danger">*</span>{" "}
                              {t("Person Count Per Serving")}
                            </label>
                            <Field
                              type="text"
                              id="personCountPerServing"
                              name="personCountPerServing"
                              placeholder={t("E.g. 1")}
                              className={`form-control ${
                                touched.personCountPerServing &&
                                errors.personCountPerServing
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="personCountPerServing" />
                          </div>
                        </div>

                        {/* <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="calorieCount">
                              <span className="text-danger">*</span> Calories
                              for Calculations
                            </label>
                            <Field
                              type="text"
                              id="calorieCount"
                              name="calorieCount"
                              placeholder={t("E.g. 200")}
                              className={`form-control ${
                                touched.calorieCount && errors.calorieCount
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError
                              name="calorieCount"
                              
                            />
                          </div>
                        </div> */}

                        <div className="col-lg-6">
                          <div className="mt-2">
                            <label htmlFor="disabledBranches">
                              {t("Select Disable Branches")}
                            </label>
                            <Select
                              isMulti={true}
                              value={values.disabledBranches}
                              onChange={item => {
                                if (
                                  item?.find(option => option.value === "*")
                                ) {
                                  setFieldValue(
                                    "disabledBranches",
                                    branchListOptions
                                  )
                                  setIsAllSelected(true)
                                } else {
                                  setFieldValue("disabledBranches", item)
                                  setIsAllSelected(
                                    item.length === branchListOptions.length
                                  )
                                }
                              }}
                              options={options}
                              className={`select2-selection ${
                                touched.disabledBranches &&
                                errors.disabledBranches
                                  ? "danger_color_border"
                                  : ""
                              } `}
                              closeMenuOnSelect={false}
                              placeholder={t("Select Disable Branches")}
                            />
                            <TextError name="disabledBranches" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="isDiscountAllowed">
                              {t("Do You Want To Add Item Group")}
                            </label>
                            <Switch
                              isChecked={values.isItemGroup}
                              onClickHandler={() => {
                                setFieldValue(
                                  "isItemGroup",
                                  !values.isItemGroup
                                )
                              }}
                            />
                          </div>
                        </div>

                        {values.isItemGroup && (
                          <div className="col-lg-6 mt-3">
                            <div className="">
                              <label htmlFor="isDiscountAllowed">
                                {t("Select Item Group")}
                              </label>
                              <Select
                                value={values.itemGroups}
                                onChange={item => {
                                  getItemsFromItemGroup(
                                    item.value,
                                    setFieldValue
                                  )
                                  setFieldValue("itemGroups", item)
                                }}
                                options={itemGroupsList?.map(item => ({
                                  label:
                                    language === "sa" ? item.nameAr : item.name,
                                  value: item._id,
                                }))}
                                className={`select2-selection`}
                                placeholder={t("Select Item Group")}
                              />
                            </div>
                          </div>
                        )}

                        {values.isItemGroup &&
                          Object.keys(values?.itemGroups).length > 0 && (
                            <div className="col-lg-12 mt-3">
                              <div
                                style={{
                                  background: "#f6f6f6",
                                  padding: 15,
                                  borderRadius: 8,
                                }}
                              >
                                <div className="d-flex justify-content-between align-items-center">
                                  <label>{values?.itemGroups?.label}</label>
                                  <div className="d-flex align-items-center">
                                    <label
                                      htmlFor="isPriceCustomize"
                                      className="mb-0 me-3"
                                    >
                                      {t("Do You Want To Customize Price")}
                                    </label>
                                    <Switch
                                      isChecked={values.isPriceCustomize}
                                      onClickHandler={() => {
                                        if (values.isPriceCustomize) {
                                          // Switching off custom prices
                                          const updatedItems =
                                            values?.selectedGroupItems?.map(
                                              item => {
                                                const originalItem =
                                                  itemGroupDetails?.items?.find(
                                                    i => i._id === item._id
                                                  )
                                                return {
                                                  ...item,
                                                  price: originalItem
                                                    ? originalItem.price
                                                    : item.price,
                                                }
                                              }
                                            )
                                          setFieldValue(
                                            "selectedGroupItems",
                                            updatedItems
                                          )
                                        }
                                        setFieldValue(
                                          "isPriceCustomize",
                                          !values.isPriceCustomize
                                        )
                                      }}
                                    />
                                  </div>
                                </div>
                                {itemGroupLoading ? (
                                  <ShimmerThumbnail height={100} rounded />
                                ) : (
                                  <Table>
                                    <thead className="">
                                      <tr>
                                        <th>{t("Items")}</th>
                                        {values.isPriceCustomize && (
                                          <th>{t("Original Price")} (SAR)</th>
                                        )}
                                        <th style={{ width: 300 }}>
                                          {t("Price")} (SAR)
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {values?.selectedGroupItems?.map(
                                        (item, index) => (
                                          <tr key={item._id}>
                                            <td>{item.name}</td>
                                            {values.isPriceCustomize && (
                                              <td>{item.originalPrice}</td>
                                            )}
                                            <td style={{ maxWidth: 300 }}>
                                              {values.isPriceCustomize ? (
                                                <>
                                                  <Field
                                                    name={`selectedGroupItems[${index}].price`}
                                                    type="number"
                                                    min="0"
                                                    step="1"
                                                    className={`form-control ${
                                                      getIn(
                                                        touched,
                                                        `selectedGroupItems[${index}].price`
                                                      ) &&
                                                      getIn(
                                                        errors,
                                                        `selectedGroupItems[${index}].price`
                                                      )
                                                        ? "is-invalid"
                                                        : ""
                                                    }`}
                                                  />
                                                  <TextError
                                                    name={`selectedGroupItems[${index}].price`}
                                                  />
                                                </>
                                              ) : (
                                                item.price
                                              )}
                                            </td>
                                          </tr>
                                        )
                                      )}
                                    </tbody>
                                  </Table>
                                )}
                              </div>
                            </div>
                          )}

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="hasSpecificDateAndTime">
                              {t("Has Specific Date And Time")}
                            </label>
                            <Switch
                              isChecked={values.hasSpecificDateAndTime}
                              onClickHandler={() => {
                                setFieldValue(
                                  "hasSpecificDateAndTime",
                                  !values.hasSpecificDateAndTime
                                )
                              }}
                            />
                          </div>
                        </div>
                        {values.hasSpecificDateAndTime && (
                          <>
                            <div className="col-lg-6">
                              <div className="form_field">
                                <label htmlFor="startDateAndTime">
                                  <span className="text-danger">*</span>{" "}
                                  {t("Start Date & Time")}
                                </label>
                                <DatePicker
                                  showTime={{ format: "HH:mm" }}
                                  format="yyyy-MM-dd HH:mm"
                                  onChange={value => {
                                    setFieldValue("startDateAndTime", value)
                                  }}
                                  value={values.startDateAndTime}
                                  placement="autoVerticalStart"
                                  placeholder={t("Start Date & Time")}
                                  disabledDate={disablePastDates}
                                  className={`date_picker ${
                                    errors.startDateAndTime &&
                                    touched.startDateAndTime
                                      ? "invalid"
                                      : ""
                                  }`}
                                />

                                <TextError
                                  name="startDateAndTime"
                                  component="div"
                                  className="text-danger"
                                />
                              </div>
                            </div>
                            <div className="col-lg-6">
                              <div className="form_field">
                                <label htmlFor="endDateAndTime">
                                  <span className="text-danger">*</span>{" "}
                                  {t("End Date & Time")}
                                </label>
                                <DatePicker
                                  showTime={{ format: "HH:mm" }}
                                  format="yyyy-MM-dd HH:mm"
                                  onChange={value => {
                                    setFieldValue("endDateAndTime", value)
                                  }}
                                  value={values.endDateAndTime}
                                  placement="autoVerticalStart"
                                  placeholder={t("End Date & Time")}
                                  disabledDate={disablePastDates}
                                  className={`date_picker ${
                                    errors.endDateAndTime && touched.endDateAndTime
                                      ? "invalid"
                                      : ""
                                  }`}
                                />

                                <TextError
                                  name="endDateAndTime"
                                  component="div"
                                  className="text-danger"
                                />
                              </div>
                            </div>
                          </>
                        )}

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="imageType">
                              <span className="text-danger">*</span>{" "}
                              {t("Image Type")}
                            </label>
                            <Field
                              as="select"
                              id="imageType"
                              name="imageType"
                              className={`form-control ${
                                touched.imageType && errors.imageType
                                  ? "is-invalid"
                                  : ""
                              }`}
                              onChange={e => {
                                const selectedValue = e.target.value
                                setFieldValue("imageType", selectedValue)
                                // Reset the value of imageURL and imageFile when the image type changes
                                setFieldValue("imageUrl", "")
                                setFieldValue("imageFile", null)
                              }}
                            >
                              <option value="URL">{t("Image Url")}</option>
                              <option value="UPLOAD">
                                {t("Upload Image")}
                              </option>
                            </Field>
                            <TextError name="imageType" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            {/* IMAGE URL */}
                            {values.imageType === "URL" && (
                              <div className="form_field">
                                <label htmlFor="imageUrl">
                                  <span className="text-danger">*</span>{" "}
                                  {t("URL")}
                                </label>
                                <Field
                                  type="text"
                                  id="imageUrl"
                                  name="imageUrl"
                                  className={`form-control ${
                                    touched.imageUrl && errors.imageUrl
                                      ? "is-invalid"
                                      : ""
                                  }`}
                                />
                                <TextError
                                  name="imageUrl"
                                  component="div"
                                  className="text-danger"
                                />
                              </div>
                            )}
                            {/* UPLOAD IMAGE */}
                            {values.imageType === "UPLOAD" && (
                              <div className="form_field position-relative">
                                <label htmlFor="imageFile">
                                  {" "}
                                  <span className="text-danger">*</span>{" "}
                                  {t("Upload Image")}
                                </label>
                                <TranslatedFileInput
                                  id="imageFile"
                                  name="imageFile"
                                  invalid={
                                    !!(touched.imageFile && errors.imageFile)
                                  }
                                  accept="image/jpeg, image/jpg, image/png"
                                  onChange={e => {
                                    handleFileChange(
                                      e,
                                      "imageFile",
                                      setFieldValue,
                                      itemWidth,
                                      itemHeight,
                                      itemMaxSize
                                    )
                                  }}
                                />
                                {isImageCompressing && (
                                  <Spinner
                                    color="primary"
                                    className="file_upload_btn"
                                  />
                                )}
                              </div>
                            )}
                            <TextError name="imageFile" />
                          </div>
                          {getImagePreviewSrc("add", values) && (
                            <div className="my-3">
                              <img
                                src={getImagePreviewSrc("add", values)}
                                alt="Preview"
                                className="img_preview"
                              />
                            </div>
                          )}
                          <p className="">
                            {t(
                              "Recommended dimensions: Square Image and Size should be less than or equal to {{size}} KB",
                              {
                                width: itemWidth,
                                height: itemHeight,
                                size: itemMaxSize,
                              }
                            )}
                          </p>
                        </div>

                        <div className="mt-3"></div>
                        <div className="col-12 d-flex justify-content-end">
                          <Button
                            type="button"
                            className="mx-2"
                            color="secondary"
                            onClick={() => navigate(-1)}
                          >
                            {t("Cancel")}
                          </Button>

                          <Button
                            type="submit"
                            color="success"
                            disabled={isLoading || isImageCompressing}
                            className="w_fit_content"
                          >
                            {mode === "add" ? t("Add") : t("Save")}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default AddCombo
